import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useHolidays, useNextHoliday } from '@/hooks/useHolidays';
import { PartyPopper } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import { MONTHS_EN } from '@/constants/months';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

export default function HolidaysPage() {
  const { language } = useAppLanguage();
  const { data: upcomingHolidays, isLoading, isError } = useHolidays();
  const { holiday: nextHoliday, daysUntil: daysUntilNext } = useNextHoliday();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = MONTHS_EN[date.getMonth()];
    return { day, month };
  };

  const getDaysUntil = (dateStr: string) => {
    const date = new Date(dateStr);
    const diff = date.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('holidays', language)} />
        <div style={styles.content}>
          <div style={styles.loadingText}>Loading holidays...</div>
        </div>
      </DetailPageLayout>
    );
  }

  if (isError) {
    return (
      <DetailPageLayout>
        <DetailPageHeader title={getTranslation('holidays', language)} />
        <div style={styles.content}>
          <div style={styles.errorText}>Unable to load holidays. Please try again later.</div>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout>
      <DetailPageHeader title={getTranslation('holidays', language)} />

      <div style={styles.content}>
        {/* Next Holiday Hero Card */}
        {nextHoliday && (
          <div style={styles.nextHolidayCard}>
            <div style={styles.nextHolidayLabel}>{getTranslation('nextHoliday', language).toUpperCase()}</div>
            <h2 style={styles.nextHolidayName}>{nextHoliday.localName}</h2>
            <div style={styles.nextHolidayLocalName}>{nextHoliday.name}</div>
            <div style={styles.nextHolidayDate}>
              <div style={styles.nextHolidayDay}>{formatDate(nextHoliday.date).day}</div>
              <div style={styles.nextHolidayMonth}>{formatDate(nextHoliday.date).month}</div>
            </div>
            <p style={styles.nextHolidayCountdown}>
              {daysUntilNext} {getTranslation('daysUntil', language)}
            </p>
          </div>
        )}

        {/* Upcoming Holidays List */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{getTranslation('upcomingHolidays', language)}</h2>

          {upcomingHolidays.length === 0 ? (
            <div style={styles.emptyState}>
              <PartyPopper size={48} color={Colors.border} />
              <p style={styles.emptyText}>{getTranslation('noResults', language)}</p>
            </div>
          ) : (
            <div style={styles.holidaysList}>
              {upcomingHolidays.map((holiday) => {
                const daysUntil = getDaysUntil(holiday.date);
                const isToday = daysUntil === 0;
                const dateInfo = formatDate(holiday.date);

                return (
                  <div key={holiday.date + holiday.name} style={styles.holidayItem}>
                    {/* Date Box */}
                    <div style={styles.dateBox}>
                      <div style={styles.dateDay}>{dateInfo.day}</div>
                      <div style={styles.dateMonth}>{dateInfo.month}</div>
                    </div>

                    {/* Holiday Info */}
                    <div style={styles.holidayInfo}>
                      <h3 style={styles.holidayName}>{holiday.localName}</h3>
                      <div style={styles.localName}>{holiday.name}</div>
                    </div>

                    {/* Countdown */}
                    {!isToday && daysUntil <= 30 && (
                      <div style={styles.countdownText}>
                        {daysUntil} {getTranslation('daysUntil', language)}
                      </div>
                    )}

                    {isToday && <div style={styles.todayBadge}>{getTranslation('today', language)}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ height: '40px' }} />
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    padding: '24px',
  } as React.CSSProperties,

  loadingText: {
    padding: '40px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
  } as React.CSSProperties,

  errorText: {
    padding: '40px',
    textAlign: 'center' as const,
    color: Colors.error,
  } as React.CSSProperties,

  nextHolidayCard: {
    backgroundColor: Colors.primary,
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '32px',
    textAlign: 'center' as const,
    color: Colors.surface,
  } as React.CSSProperties,

  nextHolidayLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.7)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '12px',
  } as React.CSSProperties,

  nextHolidayName: {
    fontSize: '24px',
    fontWeight: '700',
    color: Colors.surface,
    margin: 0,
    marginBottom: '4px',
  } as React.CSSProperties,

  nextHolidayLocalName: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: '12px',
  } as React.CSSProperties,

  nextHolidayDate: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '12px',
  } as React.CSSProperties,

  nextHolidayDay: {
    fontSize: '48px',
    fontWeight: '700',
    color: Colors.surface,
  } as React.CSSProperties,

  nextHolidayMonth: {
    fontSize: '20px',
    fontWeight: '600',
    color: Colors.surface,
  } as React.CSSProperties,

  nextHolidayCountdown: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.8)',
    margin: 0,
    textTransform: 'capitalize' as const,
  } as React.CSSProperties,

  section: {
    marginBottom: '32px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
    marginBottom: '16px',
  } as React.CSSProperties,

  holidaysList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  } as React.CSSProperties,

  holidayItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  dateBox: {
    width: '60px',
    height: '60px',
    backgroundColor: Colors.primaryLight,
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  dateDay: {
    fontSize: '24px',
    fontWeight: '700',
    color: Colors.primary,
    lineHeight: '1',
  } as React.CSSProperties,

  dateMonth: {
    fontSize: '11px',
    fontWeight: '600',
    color: Colors.primary,
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  holidayInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  } as React.CSSProperties,

  holidayName: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,

  localName: {
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  countdownText: {
    fontSize: '12px',
    fontWeight: '600',
    color: Colors.primary,
    minWidth: '60px',
    textAlign: 'right' as const,
  } as React.CSSProperties,

  todayBadge: {
    fontSize: '12px',
    fontWeight: '700',
    color: Colors.accent,
    minWidth: '60px',
    textAlign: 'right' as const,
  } as React.CSSProperties,

  emptyState: {
    padding: '40px 24px',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '12px',
  } as React.CSSProperties,

  emptyText: {
    fontSize: '14px',
    color: Colors.textSecondary,
    margin: 0,
  } as React.CSSProperties,
};
